// Copyright 2020 Sorama B.V.

import { computedFrom, observable } from "aurelia-framework";
import { Quad } from "datatypes/Generic/Quad";
import {
    CanvasMediaObjectLinkDTO,
    LayeredLinkProperties,
    LinkedMediaObjectType,
    TargetAndSourceRegionLinkProperties,
    TargetRegionLinkProperties,
} from "datatypes/SoramaWebApi";
import { ObjectExtensions } from "extensions/ObjectExtensions";
import { AffineTransformFinder } from "libraries/Math/AffineTransformFinder";
import { Matrix } from "libraries/Math/Matrix";
import { ICanvasMediaObjectLinkRepository } from "repositories/CanvasMediaObjectLinkRepository";
import { BoundingBox, IComposableChild, IComposableParent } from "../ComposableCanvas";

enum TransformMode {
    translate,
    resize,
    none,
}

export enum ComposableContentType {
    /**
     * Indicates that the composable content should be scaled and stretched.
     */
    scaled,

    /**
     * Indicates that the composable content should not be scaled and should be rendered at the default scale. (eg text)
     */
    fixedScale,
}

export interface IComposableMediaObjectAction {
    readonly icon: string;
    readonly action: () => void;
}

export interface IComposableMediaObjectSettings {
    /**
     * HTML template for this composable media object.
     */
    readonly template: string;

    /**
     * True if the composable media object as a aspect ratio that should (normally) be preserved when scaling.
     */
    readonly preserveAspectRatio: boolean;

    /**
     * Type of content in the composable media object.
     */
    readonly contentType: ComposableContentType;

    /**
     * Additional actions to show when adjusting a composable media object
     */
    readonly additionalActions: IComposableMediaObjectAction[];
}

export abstract class ComposableMediaObject implements IComposableChild, IComposableMediaObjectSettings {
    protected mediaObjectTransform: string;
    protected transformable = false;

    @computedFrom("actualTargetRegion", "canvasSize")
    public get boundingBox(): BoundingBox {
        const boundingQuad = Quad.scale(
            Quad.quadBoundingBox(this.actualTargetRegion),
            this.canvasSize.width,
            this.canvasSize.height
        );
        return {
            x: boundingQuad.topLeft.x,
            y: boundingQuad.topLeft.y,
            width: boundingQuad.topRight.x - boundingQuad.topLeft.x,
            height: boundingQuad.bottomLeft.y - boundingQuad.topLeft.y,
        };
    }

    @computedFrom("boundingBox")
    public get safeBoundingBox(): BoundingBox {
        const bb = this.boundingBox;
        const x = Math.max(bb.x, 0);
        const y = Math.max(bb.y, 0);
        const w = Math.min(bb.x + bb.width, this.canvasSize.width) - x;
        const h = Math.min(bb.y + bb.height, this.canvasSize.height) - y;
        return {
            x: x,
            y: y,
            width: w,
            height: h,
        };
    }

    @computedFrom("link.properties.layer")
    public get layer(): number {
        const layer = (<LayeredLinkProperties>this.link.properties).layer;
        return layer === undefined ? 0 : Number(layer);
    }

    @computedFrom("link.id")
    public get linkId(): string {
        return this.link.id;
    }

    @computedFrom("link.mediaObjectId")
    public get mediaObjectId(): string {
        return this.link.mediaObjectId;
    }

    public abstract readonly mediaObjectType: LinkedMediaObjectType;

    @computedFrom("contentType")
    protected get scaled(): boolean {
        return this.contentType === ComposableContentType.scaled;
    }

    @computedFrom("parent.readOnly", "transformable")
    protected get editable(): boolean {
        return !this.parent.readOnly && this.transformable;
    }

    // Settable by inherting classes
    protected busy = false;

    @observable
    protected dragElement: HTMLElement;

    //#region IComposableMediaObjectSettings

    public abstract readonly template: string;
    public abstract readonly preserveAspectRatio: boolean;
    public abstract readonly contentType: ComposableContentType;
    public abstract readonly additionalActions: IComposableMediaObjectAction[];

    //#endregion

    protected canvasSize: BoundingBox = { x: 0, y: 0, width: 1, height: 1 };

    private actualTargetRegion: Quad;
    private transformOriginRegion: Quad;

    private transformMode: TransformMode = TransformMode.none;
    private clickOrigin: { x: number; y: number };
    private resizeAnchor: { x: number; y: number };
    private resizeMask: { x: number; y: number };
    private resizeVectorLength: number;
    private resizeMaintainAspect = true;

    // Update promise that ensures a single update at a time and that updates do no interleave causing weird effects.
    private updating: boolean;
    private updatePromise: Promise<void>;

    protected constructor(
        private readonly canvasMediaObjectLinkRepository: ICanvasMediaObjectLinkRepository,
        private readonly parent: IComposableParent,
        private readonly link: CanvasMediaObjectLinkDTO,
        initialSize: BoundingBox
    ) {
        const target = (<TargetRegionLinkProperties>this.link.properties).targetRegion;
        this.actualTargetRegion = target ? target : Quad.unitQuad;
        this.canvasSize = initialSize;
        this.updateTransform();
    }

    public setComposableAreaSize(area: BoundingBox): void {
        this.canvasSize = area;
        this.updateTransform();
    }

    protected dragElementChanged(newElement: HTMLElement, oldElement: HTMLElement) {
        if (oldElement) {
            // Remove old handlers
            oldElement.onmousedown = undefined;
            window.removeEventListener("mousemove", (ev) => this.onMove(ev));
            window.removeEventListener("mouseup", (ev) => this.onUp(ev));
        }

        if (newElement) {
            newElement.onmousedown = (ev) => this.initiateTranslate(ev);
            window.addEventListener("mousemove", (ev) => this.onMove(ev));
            window.addEventListener("mouseup", (ev) => this.onUp(ev));
        }
    }

    protected toggleTransformable() {
        this.transformable = !this.transformable;
    }

    protected layerUp() {
        // Queue an update
        this.queueUpdate((props) => {
            const layer = this.parent.getNextLayerUp(this.layer);
            props.layer = layer;
            return Promise.resolve(props);
        }).catch((e) => {
            console.error(e);
        });
    }

    protected layerDown() {
        // Queue an update
        this.queueUpdate((props) => {
            const layer = this.parent.getNextLayerDown(this.layer);
            props.layer = layer;
            return Promise.resolve(props);
        }).catch((e) => {
            console.error(e);
        });
    }

    protected delete() {
        this.parent.delete(this).then(() => {
            // Unset ui handlers
            this.dragElement = undefined;
        });
    }

    protected initiateResize(
        anchor: { x: number; y: number },
        event: MouseEvent,
        scaleMask: { x: number; y: number } = { x: 1, y: 1 }
    ) {
        if (!this.editable || this.transformMode != TransformMode.none) {
            // Ignore
            return;
        }

        this.transformMode = TransformMode.resize;
        this.resizeAnchor = anchor;
        this.clickOrigin = this.getClickCoordinate(event);
        this.transformOriginRegion = this.actualTargetRegion;
        this.resizeMaintainAspect = !event.ctrlKey && this.preserveAspectRatio;
        this.resizeVectorLength =
            Math.pow(this.clickOrigin.x - this.resizeAnchor.x, 2) +
            Math.pow(this.clickOrigin.y - this.resizeAnchor.y, 2);
        this.resizeMask = scaleMask;
    }

    private initiateTranslate(ev: MouseEvent) {
        if (!this.editable || this.transformMode != TransformMode.none) {
            // Ignore
            return;
        }

        this.transformMode = TransformMode.translate;
        this.clickOrigin = this.getClickCoordinate(ev);
        this.transformOriginRegion = this.actualTargetRegion;
    }

    private onMove(ev: MouseEvent) {
        const click = this.getClickCoordinate(ev);
        switch (this.transformMode) {
            case TransformMode.resize: {
                const ax = this.resizeAnchor.x / this.canvasSize.width;
                const ay = this.resizeAnchor.y / this.canvasSize.height;
                if (this.resizeMaintainAspect) {
                    const dclick =
                        Math.pow(click.x - this.resizeAnchor.x, 2) + Math.pow(click.y - this.resizeAnchor.y, 2);
                    const scale = Math.sqrt(dclick / this.resizeVectorLength);
                    this.actualTargetRegion = Quad.scale(this.transformOriginRegion, scale, scale, ax, ay);
                } else {
                    const dx =
                        (this.resizeMask.x * (click.x - this.resizeAnchor.x)) /
                            (this.clickOrigin.x - this.resizeAnchor.x) +
                        (1 - this.resizeMask.x);
                    const dy =
                        (this.resizeMask.y * (click.y - this.resizeAnchor.y)) /
                            (this.clickOrigin.y - this.resizeAnchor.y) +
                        (1 - this.resizeMask.y);
                    this.actualTargetRegion = Quad.scale(this.transformOriginRegion, dx, dy, ax, ay);
                }

                break;
            }
            case TransformMode.translate: {
                this.actualTargetRegion = Quad.translate(
                    this.transformOriginRegion,
                    (click.x - this.clickOrigin.x) / this.canvasSize.width,
                    (click.y - this.clickOrigin.y) / this.canvasSize.height
                );
                break;
            }
            default:
                return;
        }

        this.updateTransform();
    }

    private onUp = (ev: MouseEvent) => {
        if (this.transformMode != TransformMode.none) {
            this.transformMode = TransformMode.none;

            // Queue an update
            this.queueUpdate((props) => {
                props.targetRegion = this.actualTargetRegion;
                return Promise.resolve(props);
            }).catch((e) => {
                console.error(e);
                // Revert the transform
                this.actualTargetRegion = this.transformOriginRegion;
            });
        }
    };

    private updateTransform() {
        if (this.actualTargetRegion) {
            // Create a scaling matrix to transform from [width,height] to [1,1]
            const invScale = Matrix.scale(1.0 / this.canvasSize.width, 1.0 / this.canvasSize.height, 1.0, 1.0);

            // Find affine transform in the unit coordinates
            const affineTransform = AffineTransformFinder.findTransform4(Quad.unitQuad, this.actualTargetRegion);

            // Now create another scaling matrix to revert the original one
            const scale = Matrix.scale(this.canvasSize.width, this.canvasSize.height, 1.0, 1.0);

            const totalTransform = Matrix.multiply(scale, Matrix.multiply(affineTransform, invScale));
            this.mediaObjectTransform = AffineTransformFinder.toCSSTransform(totalTransform);
        } else {
            this.mediaObjectTransform = "";
        }
    }

    private getClickCoordinate(ev: MouseEvent): { x: number; y: number } {
        return {
            x: ev.clientX - this.canvasSize.x,
            y: ev.clientY - this.canvasSize.y,
        };
    }

    private queueUpdate(
        mutation: (properties: TargetAndSourceRegionLinkProperties) => Promise<TargetAndSourceRegionLinkProperties>
    ): Promise<void> {
        this.updating = true;
        const promise = this.updatePromise ? this.updatePromise : Promise.resolve();

        // Create one promise that throws (for feedback to the queuer)
        const errorPromise = promise.then(() => {
            // Clone current properties
            const copiedProperties = <TargetAndSourceRegionLinkProperties>ObjectExtensions.copy(this.link.properties);
            return mutation(copiedProperties).then((mutated) => {
                // Apply update in the backend
                return this.canvasMediaObjectLinkRepository.updateProperties(this.link.id, mutated).then(() => {
                    // Update our actual properties
                    this.link.properties = mutated;
                });
            });
        });

        // Now catch exceptions for the queue promise
        this.updatePromise = errorPromise
            .catch(() => {})
            .then(() => {
                this.updating = false;
            });

        return errorPromise;
    }
}
